import { 
  Autocomplete, 
  FormControl, 
  TextField, 
  useTheme, 
  SxProps, 
  Theme 
} from "@mui/material";


type AutocompleteItem = {
  id: number | string;
  name: string;
};

export type ErrorStatus =
  | { tag: 'Submited'; errors: string[] }
  | { tag: 'Not submitted' };

interface DropDownMultiAutocompleteProps<T extends AutocompleteItem> {  
  label: string;
  options: T[];
  value: T[];  
  onChange: (values: T[]) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  errors?: ErrorStatus;
  helperText?: string;
  sx?: SxProps<Theme>; 
}

/**
 * DropDownMultiAutocompleteComponent
 *
 * Generic multi-select dropdown component based on MUI Autocomplete.
 * Designed for selecting multiple items from a predefined list with
 * search, checkbox-like behaviour and validation support.
 *
 * The component supports controlled selection, disabled state,
 * required indication and form-level validation errors
 * (e.g. displayed only after form submission).
 *
 * @typeParam T - Option item type. Must contain `id` and `name` fields.
 *
 * @param label - Label displayed above the input field.
 * @param options - Full list of available options to select from.
 * @param value - Currently selected options (controlled).
 * @param onChange - Callback triggered when selection changes.
 *                  Receives the full array of selected options.
 * @param placeholder - Placeholder text displayed when no value is selected.
 * @param className - Optional CSS class applied to the FormControl wrapper.
 * @param required - Marks the field as required and displays an asterisk.
 * @param disabled - Disables user interaction with the component.
 * @param errors - Validation status object.
 *                 Errors are shown only when `tag === 'Submited'`.
 * @param helperText - Helper text shown when there are no validation errors.
 * @param sx - Optional MUI `sx` style overrides applied to the Autocomplete.
 */

function DropDownMultiAutocompleteComponent<T extends AutocompleteItem>({
  className,
  label,
  placeholder,
  options,
  value,
  onChange,
  required = false,
  disabled = false,
  errors,
  helperText,
  sx
}: DropDownMultiAutocompleteProps<T>) {
  const theme = useTheme();

  const isError = errors?.tag === 'Submited' && errors.errors.length > 0;

  const errorMassage = isError ? errors.errors.join('. ') : '';
  
  return (
    <FormControl
      fullWidth 
      margin="normal"
      error={isError}
      className={className}
      >
        <Autocomplete
          multiple
          options={options}          
          value={value}
          disabled={disabled}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionLabel={(option) => option.name}
          onChange={(_, values) => onChange(values)}
          disableCloseOnSelect
          sx={sx}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label ? `${label} *` : label}
              placeholder={placeholder}
              error={isError}
              helperText={isError? errorMassage: helperText}
              required={required? required : false}
            />
          )}
          slotProps={{
            popper: {
              sx: {
                '& .MuiAutocomplete-option[aria-selected="true"]': {
                  backgroundColor: `${theme.palette.primary.main} !important`,
                  color: theme.palette.secondary.contrastText,
                },

                // selected + hover / focus
                '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                  backgroundColor: `${theme.palette.primary.dark} !important`,
                },

                // just hover
                // '& .MuiAutocomplete-option.Mui-focused': {
                //   backgroundColor: theme.palette.secondary.light,
                // },
              },
            },
          }}          
        />
      </FormControl>
  )
}

export default DropDownMultiAutocompleteComponent;